import { format } from 'date-fns'
import React, { useContext } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { useMomentDragContext } from '../MomentDragContext'
import { WeekViewGlobalContext } from '../weekViewGlobalContext'
import { setMomentDragData } from './dragData'
import defaultMomentPreview from './dragPreviews'
import * as styles from './styles'
import './styles.scss'

const withDraggable = (MomentCard) =>
  function DraggableCard({
    activeFilters,
    selectedFilters,
    contextMenuCallbacks,
    isLimitedUser,
    isLoadingMoments,
    moment,
    story,
    theme = 'light',
    track,
  }) {
    const cardRef = React.useRef(null)
    const [isDragging, setIsDragging] = React.useState(false)
    const [isDraggingRestrictedContent, setIsDraggingRestrictedContent] =
      React.useState(false)
    const { setDraggedMoment } = useMomentDragContext()
    const { setDisableActiveColumn } = useContext(WeekViewGlobalContext)

    const dragImage = new Image()
    dragImage.src = defaultMomentPreview

    const onDragMoment = (e) => {
      const {
        id,
        primaryStory,
        scheduled_at: scheduledAt,
        day_segment: daySegment,
      } = moment

      if (typeof track === 'function') {
        track('drag', { id: id, type: 'moment' })
      }

      setDisableActiveColumn(true)

      setMomentDragData(e, {
        id,
        scheduledAt,
        daySegment,
      })

      e.dataTransfer.setDragImage(dragImage, 62, 10)

      // setTimeout addresses known chrome bug that fires dragEnd immediately after dragstart
      // specifically a problem when cursor clicks to initate dragStart from below dayRow div before day offset
      setTimeout(() => {
        setIsDragging(true)
        setDraggedMoment({
          id,
          primaryStoryInterval: primaryStory
            ? {
                start: new Date(primaryStory.scheduled_start_at),
                end: new Date(primaryStory.scheduled_end_at),
              }
            : null,
          scheduledAt: new Date(scheduledAt).toISOString(),
        })
      }, 0)
    }

    const dragShadowHeight = React.useMemo(() => {
      if (!isDragging || !cardRef.current) return 0
      const boundingRect = cardRef.current.getBoundingClientRect()
      return boundingRect ? boundingRect.height : 0
    }, [isDragging])

    const onDragEnd = () => {
      setIsDragging(false)
      setDraggedMoment(null)
      setDisableActiveColumn(false)
    }

    const onKeyDown = (e) => {
      if (
        (e.keyCode !== 13 && e.keyCode !== 32) ||
        e.target !== cardRef.current ||
        e.target.outerHTML.includes('placeholder')
      )
        return
      e.target
        .querySelectorAll('.kuiper-momentCard,.kuiper-emptyMomentCard')[0]
        .click()
    }

    const isDraggable = React.useMemo(
      () =>
        [
          !moment.isPlaceholder,
          !moment.is_ghost,
          !moment.isSaving,
          !moment.isUpdatingContent,
          !isLimitedUser,
          !isLoadingMoments,
          !isDraggingRestrictedContent,
        ].every((condition) => condition),
      [moment, isLimitedUser, isLoadingMoments, isDraggingRestrictedContent]
    )

    const ariaLabel =
      `${moment.name} ` + `${format(moment.scheduled_at, 'EEEE LLLL do yyyy')}`

    const childProps = {
      activeFilters,
      selectedFilters,
      // Apply new props from HOC
      isDragging,
      onDragRestrictedContent: setIsDraggingRestrictedContent,

      // Forward all props coming into HOC
      contextMenuCallbacks,
      isLimitedUser,
      isLoadingMoments,
      moment,
      setDraggedMoment,
      story,
      theme,
      track,
    }

    return (
      <div
        aria-label={ariaLabel}
        className={kuiperClassNames('draggableCardContainer', { isDragging })}
        css={styles.container({ theme })}
        draggable={isDraggable}
        onDragEnd={onDragEnd}
        onDragStart={onDragMoment}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex="0"
        ref={cardRef}
      >
        {isDragging ? (
          <div
            className={kuiperClassNames('momentCardDragShadow', { isDragging })}
            style={{ blockSize: dragShadowHeight }}
          />
        ) : (
          <MomentCard {...childProps} />
        )}
      </div>
    )
  }

export default withDraggable
